import { useState } from 'react';
import type { ProspectListRead } from '@signalvote/shared';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useProspectListUndoPreview, useUndoProspectList } from '@/hooks/queries';
import { canUndo, keptReasons } from '@/lib/fundraising/prospectListUpload';

const count = (n: number) => n.toLocaleString();

/**
 * Undo one upload, after a confirmation that says who will be removed and who kept. Anyone called,
 * committed or on another list stays, and the list then reads partly undone.
 */
export function ProspectListUndo({ list }: { list: ProspectListRead }) {
  const [open, setOpen] = useState(false);
  const { data: preview, isLoading, error: previewError } = useProspectListUndoPreview(list.id, open);
  const { mutate: undoList, isPending, error: undoError } = useUndoProspectList();

  if (!canUndo(list.status)) return null;

  const onConfirm = () => {
    if (!preview) return;
    undoList({ id: list.id, concurrencyToken: preview.concurrencyToken }, { onSuccess: () => setOpen(false) });
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        Undo
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Undo &ldquo;{list.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                {isLoading && <p>Checking who this list brought in…</p>}
                {previewError && <p className="text-amber-700">{previewError.message}</p>}
                {preview && (
                  <>
                    <p>{count(preview.removedCount)} people will be removed.</p>
                    {preview.keptCount > 0 && (
                      <p>
                        {count(preview.keptCount)} will be kept ({keptReasons(preview)}), and the list will read
                        partly undone.
                      </p>
                    )}
                    <p>You can upload the same file again afterwards.</p>
                  </>
                )}
                {undoError && <p className="text-amber-700">{undoError.message}</p>}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Keep the list</AlertDialogCancel>
            <AlertDialogAction
              disabled={!preview || isPending}
              onClick={(e) => {
                // Stay open until the undo lands, so a refusal can be shown.
                e.preventDefault();
                onConfirm();
              }}
            >
              {isPending ? 'Undoing…' : 'Undo this list'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
